// Редактор маршрутов - рисование линии + расстановка типизированных
// вейпоинтов на карте, сохранение в собственные (owner_user_id) маршруты
// пользователя. Сознательно НЕ переиспользует useMapRuler.ts (см. план
// "route editor" §5) - отдельный composable со своими source/layer id и
// собственной, более простой моделью вершин/легов, вдохновлённой линейкой
// (клик - вершина, магнит - трассировка по /routing/path), но без её
// контекстного меню "вставить/удалить произвольную точку" - в v1 это
// осознанно не реализовано.
//
// Линия рендерится тем же движком, что и опубликованные маршруты
// (routeLineStyle.ts/routeLineStyleMaplibre.ts - buildRouteLineFeatures/
// buildRouteLinePaint) - WYSIWYG: то, что видно во время рисования, совпадает
// с тем, что получится после сохранения. Высота на вершину интерполируется
// из живого профиля (POST /routes/preview-analytics) по кумулятивной
// дистанции - редактор не хранит высоту по каждой точке сам.
//
// Загрузка УЖЕ СОХРАНЁННОГО маршрута/GPX-импорта строит ту же модель
// "вершины + леги", что и линия, нарисованная вручную - не отдельную
// неинтерактивную "основу", как было раньше (см. tasks/ - тот подход был
// вынужденным, backend пересэмплирует геометрию шагом ~30м на сохранении,
// geometry.py::_resample_points, что даёт сотни точек, непригодных для одной
// draggable-вершины на каждую). Вместо этого детальная геометрия сначала
// упрощается алгоритмом Дугласа-Пекера (utils/simplifyPath.ts) до разумного
// числа вершин, а исходная детальная форма сохраняется как geometry каждого
// лега между соседними вершинами (buildEditableGeometryFromPath ниже) - вся
// линия целиком перетаскиваема, не только добавленная поверх часть.
import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import type { FeatureCollection } from "geojson";
import { computed, markRaw, ref, shallowRef, toRaw } from "vue";
import api from "../api";
import { DEFAULT_ROUTE_STYLE, haversineDistanceM } from "../utils/routeLineStyle";
import { elevationsAtDistances } from "../utils/routeElevation";
import { planVertexMarkers } from "../utils/vertexMarkerPlan";
import { useToast } from "./useToast";
import type { PointLLE, RouteStyleConfig } from "../utils/routeLineStyle";
import {
  ROUTE_ARROW_IMAGE_ID,
  buildArrowIconImage,
  buildRouteLineFeatures,
  buildRouteLineLayout,
  buildRouteLinePaint,
} from "../utils/routeLineStyleMaplibre";
import { markerElement, PIN_SIZE, routeMapPinHtml, setGeoJsonSourceData } from "../map/mapUiHelpers";
import { POI_TYPE_MARKERS } from "../map/poiTypeMarkers";
import { ROUTE_EDITOR_LINE_SOURCE } from "../map/constants";
import type { MapCtx } from "../map/mapContext";
import { useRoutingPath, type Coordinate } from "./useRoutingPath";
import type { RouteMapItem, RoutePreviewAnalyticsResponse } from "../types/routes";
import { isPoiType, type PoiType } from "../types/poiTypes";
import { removeVertexAt } from "../utils/removeVertex";
import { clampLegSelection, toggleLegSelection } from "../utils/legSelection";
import { findNearestLeg, isClosedLine, legMidpoint, splitLegAt } from "../utils/routeLegs";
import { reverseLine } from "../utils/reverseRoute";
import { simplifyCoordinates, simplifyLineIndicesCapped, simplifyToMaxPoints } from "../utils/simplifyPath";
import { trackEvent } from "../analytics";

const GLOW_SOURCE = "route-editor-glow";
const CASING_SOURCE = "route-editor-casing";
const ARROWS_SOURCE = "route-editor-arrows";
const GLOW_LAYER = "route-editor-glow-layer";
const CASING_LAYER = "route-editor-casing-layer";
const MAIN_LAYER = "route-editor-line-layer";
const ARROWS_LAYER = "route-editor-arrows-layer";
const LEGSEL_SOURCE = "route-editor-legsel";
const LEGSEL_LAYER = "route-editor-legsel-layer";
const LINE_LAYERS = [GLOW_LAYER, CASING_LAYER, MAIN_LAYER, LEGSEL_LAYER, ARROWS_LAYER];

// Инструменты - независимые кнопки-переключатели: "line" (рисовать линию,
// липкий - пока не отжмёшь), "waypoint" (поставить одну точку - после
// подтверждения/отмены диалога сам выключается), null - ничего не нажато:
// клики по карте ничего не добавляют, вершины линии и точки можно
// перетаскивать и открывать на редактирование. "erase" (ластик) - тап по
// вершине линии или точке-отметке удаляет её.
export type EditorMode = "line" | "waypoint" | "erase" | null;
export type LineStyleMode = "single" | "gradient";

interface EditorLeg {
  geometry: Coordinate[];
  routed: boolean;
  status: "routed" | "straight" | "fallback";
}

export interface EditorWaypoint {
  clientId: string;
  name: string;
  description: string | null;
  poi_type: PoiType;
  latitude: number;
  longitude: number;
}

export interface RouteSaveMeta {
  name: string;
  description: string | null;
  tags: string[];
  is_public: boolean;
}

export interface GpxImportWaypoint {
  name: string;
  description?: string | null;
  poi_type?: string | null;
  latitude: number;
  longitude: number;
}

const makeClientId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `wp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function useRouteEditor(ctx: MapCtx) {
  const active = ref(false);
  const mode = ref<EditorMode>("line");
  // По умолчанию включён: новые отрезки сразу ложатся на тропы, а прямая -
  // осознанный выбор (см. setMagnet).
  const magnetEnabled = ref(true);
  const routing = useRoutingPath();

  // ── Вид (меню "Вид") ────────────────────────────────────────────────
  const baseRouteStyle = ref<RouteStyleConfig>(DEFAULT_ROUTE_STYLE);
  const lineStyleMode = ref<LineStyleMode>(DEFAULT_ROUTE_STYLE.mode);
  const showDirectionArrows = ref(DEFAULT_ROUTE_STYLE.arrows.enabled);
  // Один тумблер на всю нижнюю панель (профиль+статистика вместе, внутри
  // неё - свои вкладки/side-by-side) - раньше было два независимых чекбокса,
  // упразднено по просьбе пользователя как избыточное.
  const showStatsPanel = ref(true);

  const effectiveStyleConfig = computed<RouteStyleConfig>(() => ({
    ...baseRouteStyle.value,
    mode: lineStyleMode.value,
    arrows: { ...baseRouteStyle.value.arrows, enabled: showDirectionArrows.value },
  }));

  const vertices = ref<Coordinate[]>([]);
  const legs = ref<EditorLeg[]>([]);
  const waypoints = ref<EditorWaypoint[]>([]);
  // Предзаполнение диалога добавления точки (обновляется после каждого
  // подтверждённого добавления) - не гейтит поведение клика, только UX-
  // удобство "запомнил последний выбранный тип".
  const lastWaypointType = ref<PoiType>("rest-stop");
  const selectedWaypointId = ref<string | null>(null);
  // Выбранный тапом отрезок линии (индекс в legs): подсвечивается, для него в
  // контекстной панели доступен переключатель "прямая / по тропам".
  const selectedLegIndexes = ref<number[]>([]);
  // Клик по карте в режиме "Точки" не добавляет вейпоинт сразу - открывает
  // диалог подтверждения (тип/имя/описание), см. requestAddWaypointAt/
  // confirmPendingWaypoint ниже.
  const pendingWaypoint = ref<{ latitude: number; longitude: number } | null>(null);

  const currentRouteId = ref<number | null>(null);
  const isCloneOfPublic = ref(false);
  const dirty = ref(false);
  const routeMeta = ref<RouteSaveMeta>({ name: "", description: null, tags: [], is_public: false });

  const loading = ref(false);
  const saving = ref(false);
  const errorMessage = ref<string | null>(null);

  // shallowRef: ответ - тысячи точек профиля; глубокая реактивность их не нужна
  // (поле заменяется целиком), а чтение через прокси замедляло перерисовку линии.
  const analytics = shallowRef<RoutePreviewAnalyticsResponse | null>(null);
  const analyticsLoading = ref(false);

  // Кратковременное уведомление редактора (например, «сюда проложить нельзя»).
  const { message: notice, show: showNotice } = useToast(3500);
  const notifyBlocked = () => showNotice(ctx.t("map.routeEditor.routeBlocked"));

  const MAX_HISTORY = 50;
  type Snapshot = { vertices: Coordinate[]; legs: EditorLeg[]; waypoints: EditorWaypoint[] };
  const history = ref<Snapshot[]>([]);
  const future = ref<Snapshot[]>([]);
  const canUndo = computed(() => history.value.length > 0);
  const canRedo = computed(() => future.value.length > 0);

  // Снимок для истории - поверхностный: геометрия отрезков и кортежи вершин не
  // изменяются на месте (их только заменяют), поэтому массивы делятся между
  // снимками и живым состоянием, а копируются лишь «каркасы» (O(отрезков), а не
  // O(точек)). markRaw - чтобы Vue не оборачивал тысячи точек в прокси.
  const snapshot = (): Snapshot =>
    markRaw({
      vertices: toRaw(vertices.value).slice(),
      legs: toRaw(legs.value).map((leg) => ({ ...toRaw(leg) })),
      waypoints: toRaw(waypoints.value).map((w) => ({ ...toRaw(w) })),
    });

  let dirtyBeforeLastPush = false;
  const pushHistory = () => {
    dirtyBeforeLastPush = dirty.value;
    history.value.push(snapshot());
    if (history.value.length > MAX_HISTORY) history.value.shift();
    future.value = [];
    dirty.value = true;
  };

  const applySnapshot = (snap: Snapshot) => {
    vertices.value = snap.vertices;
    legs.value = snap.legs;
    waypoints.value = snap.waypoints;
    redraw();
    scheduleAnalytics();
  };

  // Откатить только что записанный шаг, не отдавая его в «повторить»: действие
  // не состоялось (маршрут проложить нельзя), а не было отменено пользователем.
  const revertLastStep = () => {
    const prev = history.value.pop();
    if (!prev) return;
    applySnapshot(prev);
    dirty.value = dirtyBeforeLastPush;
  };

  const undo = () => {
    const prev = history.value.pop();
    if (!prev) return;
    future.value.push(snapshot());
    applySnapshot(prev);
  };

  const redo = () => {
    const next = future.value.pop();
    if (!next) return;
    history.value.push(snapshot());
    applySnapshot(next);
  };

  // Разворот маршрута: старт и финиш меняются местами. Геометрия каждого
  // лега разворачивается как есть (граф троп ненаправленный, трасса не
  // перестраивается); вейпоинты остаются на своих местах - дни/участки
  // пересчитает аналитика по их положению вдоль уже развёрнутой линии.
  const reverseRoute = () => {
    if (vertices.value.length < 2) return;
    pushHistory();
    const reversed = reverseLine(vertices.value, legs.value);
    vertices.value = reversed.vertices;
    legs.value = reversed.legs;
    redraw();
    scheduleAnalytics();
  };

  // Маркеры вершин обновляются инкрементально (planVertexMarkers): vertexMarkers[i] -
  // маркер вершины i или null, vertexSigs - их сигнатуры; ручки середин (режим
  // правки) пересоздаются целиком.
  let vertexMarkers: (Marker | null)[] = [];
  let vertexSigs: (string | null)[] = [];
  const midMarkers: Marker[] = [];
  const waypointMarkers: Map<string, Marker> = new Map();
  let layersReady = false;
  let analyticsRequestId = 0;
  let analyticsTimer: number | null = null;
  let analyticsAbort: AbortController | null = null;
  let draggingVertex = false;
  let suppressNextClick = false;

  const getMap = () => ctx.getMap();

  // Прицел только пока выбран инструмент добавления (линия/точка); в режиме
  // "правка" обычный курсор - вершины и точки перетаскиваются.
  const updateCursor = () => {
    const map = getMap();
    if (map) map.getCanvas().style.cursor = active.value && (mode.value === "line" || mode.value === "waypoint") ? "crosshair" : "";
  };

  // ── Геометрия ────────────────────────────────────────────────────────

  const editedCoordinates = (): Coordinate[] => {
    if (!legs.value.length) return vertices.value.slice();
    // toRaw: тысячи вершин; чтение через реактивные прокси на порядок дороже, а
    // всем вызывающим (упрощение, аналитика, сохранение, рисование) нужны просто числа.
    const out: Coordinate[] = [];
    for (const leg of toRaw(legs.value)) {
      for (const coordinate of toRaw(leg.geometry)) {
        const last = out.at(-1);
        if (!last || coordinate[0] !== last[0] || coordinate[1] !== last[1]) out.push(coordinate);
      }
    }
    return out;
  };

  const renderedCoordinates = (): Coordinate[] => editedCoordinates();

  // Высота на вершину - линейная интерполяция по живому профилю
  // (analytics.elevation_profile.points, {distance_km, elevation_m}) через
  // накопленную дистанцию вдоль renderedCoordinates(). Редактор не хранит
  // высоту по каждой точке отдельно - только эта проекция на момент отрисовки.
  const elevatedLine = (): PointLLE[] => {
    const coords = renderedCoordinates();
    if (coords.length < 2) return [];
    const points = analytics.value?.elevation_profile?.points;
    if (!points || !points.length) {
      return coords.map(([lng, lat]) => [lat, lng, null] as PointLLE);
    }
    const cumKm: number[] = [0];
    for (let i = 1; i < coords.length; i++) {
      const segM = haversineDistanceM(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]);
      cumKm.push(cumKm[i - 1] + segM / 1000);
    }
    // toRaw: профиль - тысячи точек, чтение через реактивный прокси на порядок
    // дороже; линия перерисовывается на каждый тап и кадр перетаскивания.
    const elevations = elevationsAtDistances(cumKm, toRaw(points));
    return coords.map(([lng, lat], i) => [lat, lng, elevations[i]] as PointLLE);
  };

  const nearestOnPath = (
    path: Coordinate[],
    lng: number,
    lat: number
  ): { point: Coordinate; distDeg: number } | null => {
    if (path.length < 2) return null;
    let best: { point: Coordinate; distDeg: number } | null = null;
    for (let i = 1; i < path.length; i++) {
      const [ax, ay] = path[i - 1];
      const [bx, by] = path[i];
      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy || 1e-12;
      let t = ((lng - ax) * dx + (lat - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      const px = ax + t * dx;
      const py = ay + t * dy;
      const d = Math.hypot(lng - px, lat - py);
      if (!best || d < best.distDeg) best = { point: [px, py], distDeg: d };
    }
    return best;
  };

  const nearestOnRenderedLine = (lng: number, lat: number) => nearestOnPath(renderedCoordinates(), lng, lat);

  // Магнит для точек (при добавлении и перетаскивании): притягиваем к линии
  // маршрута, а если рядом её нет - к ближайшей тропе/дороге карты. Радиус
  // притяжения в пикселях экрана, а не градусах - одинаково на любом зуме;
  // дальше радиуса точка остаётся ровно там, где её поставили.
  const WAYPOINT_SNAP_PX = 28;
  const snapWaypointPoint = (lngLat: Coordinate): Coordinate => {
    const map = getMap();
    if (!map) return lngLat;
    const origin = map.project(lngLat);
    const within = (p: Coordinate) => {
      const q = map.project(p);
      return Math.hypot(q.x - origin.x, q.y - origin.y) <= WAYPOINT_SNAP_PX;
    };
    const onLine = nearestOnRenderedLine(lngLat[0], lngLat[1]);
    if (onLine && within(onLine.point)) return onLine.point;

    const layers = ["trails-line", "roads-line"].filter((id) => Boolean(map.getLayer(id)));
    if (!layers.length) return lngLat;
    const box: [maplibregl.PointLike, maplibregl.PointLike] = [
      [origin.x - WAYPOINT_SNAP_PX, origin.y - WAYPOINT_SNAP_PX],
      [origin.x + WAYPOINT_SNAP_PX, origin.y + WAYPOINT_SNAP_PX],
    ];
    let best: { point: Coordinate; distDeg: number } | null = null;
    for (const feature of map.queryRenderedFeatures(box, { layers })) {
      const geometry = feature.geometry;
      const lines =
        geometry.type === "LineString"
          ? [geometry.coordinates]
          : geometry.type === "MultiLineString"
            ? geometry.coordinates
            : [];
      for (const line of lines) {
        const candidate = nearestOnPath(line as Coordinate[], lngLat[0], lngLat[1]);
        if (candidate && within(candidate.point) && (!best || candidate.distDeg < best.distDeg)) best = candidate;
      }
    }
    return best ? best.point : lngLat;
  };

  // ── Отрисовка линии ─────────────────────────────────────────────────
  // Тот же движок, что у опубликованных маршрутов (см. шапку файла) - четыре
  // слоя (glow/casing/main/arrows), а не плоская одноцветная линия.

  // Только линия, без пересоздания маркеров: во время перетаскивания вершины
  // redraw() (с syncVertexMarkers) убивал бы сам перетаскиваемый маркер на
  // каждом кадре и жест обрывался.
  const redrawLine = () => {
    const map = getMap();
    if (!map || !layersReady) return;
    const points = elevatedLine();
    const features = buildRouteLineFeatures(points, effectiveStyleConfig.value);
    setGeoJsonSourceData(map, GLOW_SOURCE, features.glow);
    setGeoJsonSourceData(map, CASING_SOURCE, features.casing);
    setGeoJsonSourceData(map, ROUTE_EDITOR_LINE_SOURCE, features.main);
    setGeoJsonSourceData(map, ARROWS_SOURCE, features.arrows);
    selectedLegIndexes.value = clampLegSelection(selectedLegIndexes.value, legs.value.length);
    setGeoJsonSourceData(
      map,
      LEGSEL_SOURCE,
      selectedLegIndexes.value
        .map((i) => legs.value[i])
        .filter((leg) => leg && leg.geometry.length >= 2)
        .map((leg) => ({
          type: "Feature" as const,
          properties: {},
          geometry: { type: "LineString" as const, coordinates: leg.geometry },
        }))
    );
  };

  const redraw = () => {
    const map = getMap();
    if (!map || !layersReady) return;
    redrawLine();
    syncVertexMarkers();
    syncWaypointMarkers();
  };

  const removeAllMarkers = () => {
    for (const m of vertexMarkers) m?.remove();
    vertexMarkers = [];
    vertexSigs = [];
    while (midMarkers.length) midMarkers.pop()!.remove();
    for (const marker of waypointMarkers.values()) marker.remove();
    waypointMarkers.clear();
  };

  const applyLinePaint = () => {
    const map = getMap();
    if (!map || !layersReady) return;
    const paint = buildRouteLinePaint(effectiveStyleConfig.value);
    const layout = buildRouteLineLayout(effectiveStyleConfig.value);
    for (const [layerId, layerPaint] of [
      [GLOW_LAYER, paint.glow],
      [CASING_LAYER, paint.casing],
      [MAIN_LAYER, paint.main],
    ] as const) {
      if (!map.getLayer(layerId)) continue;
      for (const [key, value] of Object.entries(layerPaint)) map.setPaintProperty(layerId, key as any, value as any);
      for (const [key, value] of Object.entries(layout)) map.setLayoutProperty(layerId, key as any, value as any);
    }
  };

  const setLineStyleMode = (next: LineStyleMode) => {
    lineStyleMode.value = next;
    applyLinePaint();
    redraw();
  };

  const setShowDirectionArrows = (next: boolean) => {
    showDirectionArrows.value = next;
    redraw();
  };

  // ── Вершины линии ────────────────────────────────────────────────────

  // Первая вершина - старт, последняя (если вершин больше одной) - финиш:
  // цветной кружок (те же цвета, что у маркеров start/finish в профиле высот)
  // и подпись над ним из data-label (см. .route-editor-vertex--start/finish в
  // styles.css). Номер остаётся у всех, чтобы было видно порядок.
  // Замкнутая линия (финиш совпадает со стартом): одна метка "Старт/финиш" на
  // первой вершине, а последняя (лежит в той же точке) скрывается.
  const makeVertexElement = (index: number, total: number, closed: boolean): HTMLElement => {
    const el = document.createElement("div");
    el.className = "route-editor-vertex";
    el.textContent = String(index + 1);
    el.setAttribute("role", "button");
    if (index === 0) {
      el.classList.add("route-editor-vertex--start");
      if (closed) el.classList.add("route-editor-vertex--loop");
      el.dataset.label = ctx.t(closed ? "map.routeEditor.startFinishLabel" : "map.routeEditor.startLabel");
    } else if (index === total - 1) {
      if (closed) {
        el.classList.add("route-editor-vertex--merged");
      } else {
        el.classList.add("route-editor-vertex--finish");
        el.dataset.label = ctx.t("map.routeEditor.finishLabel");
      }
    }
    return el;
  };

  // Перетаскивание вершины: живое обновление смежных легов прямыми (для
  // превью) и фиксация + перекладка по графу после отпускания.
  const dragVertexTo = (index: number, ll: { lng: number; lat: number }) => {
    vertices.value[index] = [ll.lng, ll.lat];
    for (const legIndex of [index - 1, index]) {
      const leg = legs.value[legIndex];
      if (leg && vertices.value[legIndex]) leg.geometry = [vertices.value[legIndex], vertices.value[legIndex + 1]];
    }
    redrawLine();
  };

  // Начало перетаскивания вершины (и ручки середины): один шаг истории на жест.
  const beginVertexDrag = () => {
    pushHistory();
    draggingVertex = true;
  };

  // Отпустили вершину: смежные отрезки перекладываются по графу. Если проложить
  // нельзя (река/обрыв, обхода нет) - вершина и отрезки возвращаются как были.
  const finishVertexDrag = async (index: number, ll: { lng: number; lat: number }) => {
    vertices.value[index] = [ll.lng, ll.lat];
    draggingVertex = true;
    suppressNextClick = true;
    window.setTimeout(() => {
      draggingVertex = false;
    }, 0);
    if (!(await rebuildAdjacentLegs(index))) {
      revertLastStep();
      notifyBlocked();
    }
  };

  // Ручка на середине отрезка (режим правки): потянуть за неё - создать новую
  // вершину и сразу вести её. На телефоне жест по самой линии двигает карту,
  // поэтому нужна отдельная ручка. Лимит - чтобы длинный GPX не завалил DOM.
  const MAX_MIDPOINT_HANDLES = 300;
  const addMidpointHandles = (map: maplibregl.Map) => {
    if (legs.value.length > MAX_MIDPOINT_HANDLES) return;
    legs.value.forEach((leg, legIndex) => {
      if (leg.geometry.length < 2) return;
      const mid = legMidpoint(leg.geometry);
      const element = document.createElement("div");
      element.className = "route-editor-midpoint";
      element.setAttribute("role", "button");
      const marker = new maplibregl.Marker({ element, draggable: true }).setLngLat(mid).addTo(map);
      const newIndex = legIndex + 1;
      marker.on("dragstart", () => {
        beginVertexDrag();
        const out = splitLegAt(vertices.value, legs.value, legIndex, mid);
        vertices.value = out.vertices;
        legs.value = out.legs;
        redrawLine();
      });
      marker.on("drag", () => dragVertexTo(newIndex, marker.getLngLat()));
      marker.on("dragend", () => void finishVertexDrag(newIndex, marker.getLngLat()));
      midMarkers.push(marker);
    });
  };

  const createVertexMarker = (
    map: maplibregl.Map,
    index: number,
    coord: Coordinate,
    total: number,
    closed: boolean,
    isLine: boolean,
    erasing: boolean
  ): Marker => {
    const element = makeVertexElement(index, total, closed);
    if (!isLine) element.classList.add("route-editor-vertex--static");
    const marker = new maplibregl.Marker({ element, draggable: isLine && !erasing }).setLngLat(coord).addTo(map);
    if (erasing) {
      // Ластик: тап по вершине удаляет её. stopPropagation - клик на
      // DOM-маркере иначе всплыл бы до map "click".
      element.classList.add("route-editor-vertex--erase");
      element.addEventListener("click", (e) => {
        e.stopPropagation();
        void deleteVertex(index);
      });
      return marker;
    }
    if (!isLine) return marker;
    marker.on("dragstart", beginVertexDrag);
    marker.on("drag", () => dragVertexTo(index, marker.getLngLat()));
    marker.on("dragend", () => void finishVertexDrag(index, marker.getLngLat()));
    return marker;
  };

  const syncVertexMarkers = () => {
    const map = getMap();
    if (!map) return;
    while (midMarkers.length) midMarkers.pop()!.remove();
    // В режиме "Точки" обычные вершины не мешают расстановке, но старт и
    // финиш остаются видны - как некликабельные (pointer-events: none)
    // метки, клик под ними уходит в карту.
    const isLine = mode.value !== "waypoint";
    const erasing = mode.value === "erase";
    const closed = isClosedLine(vertices.value);
    const total = vertices.value.length;
    const raw = toRaw(vertices.value);
    const next = raw.map((coord, index): string | null => {
      const isEnd = index === 0 || index === total - 1;
      if (!isLine && !isEnd) return null;
      const role = index === 0 ? "s" : index === total - 1 ? "f" : "m";
      return `${index}|${coord[0]}|${coord[1]}|${role}|${closed ? 1 : 0}|${isLine ? 1 : 0}|${erasing ? 1 : 0}`;
    });
    const plan = planVertexMarkers(vertexSigs, next);
    for (const i of plan.remove) {
      vertexMarkers[i]?.remove();
      vertexMarkers[i] = null;
    }
    vertexMarkers.length = next.length;
    for (const i of plan.create) vertexMarkers[i] = createVertexMarker(map, i, raw[i], total, closed, isLine, erasing);
    vertexSigs = next;
    // Ручки середин отрезков - только в режиме правки (ничего не нажато).
    if (mode.value === null) addMidpointHandles(map);
  };

  // false - проложить нельзя (status "blocked": на пути река/обрыв, обхода нет);
  // тогда отрезок не меняется, решение (откат/прямая) принимает вызывающий.
  const routeLeg = async (index: number): Promise<boolean> => {
    const leg = legs.value[index];
    if (!leg?.routed) return true;
    const start = vertices.value[index];
    const end = vertices.value[index + 1];
    if (!start || !end) return true;
    const result = await routing.route(start, end, { strategy: "shortest", snapRadiusM: 100 });
    if (!result) return true;
    if (result.status === "blocked") return false;
    legs.value[index] = {
      ...leg,
      geometry: result.geometry,
      status: result.status === "routed" ? "routed" : "fallback",
    };
    return true;
  };

  const rebuildAdjacentLegs = async (vertexIndex: number): Promise<boolean> => {
    for (const index of [vertexIndex - 1, vertexIndex]) {
      if (index >= 0 && index < legs.value.length && !(await routeLeg(index))) return false;
    }
    redraw();
    scheduleAnalytics();
    return true;
  };

  // Удаление вершины (ластик). Крайняя вершина просто отрезает свой лег;
  // внутренняя склеивает два соседних лега в один прямой между оставшимися
  // соседями, а если хоть один из них был проложен по графу - перекладывается
  // по графу заново (как при перетаскивании).
  const deleteVertex = async (index: number) => {
    if (index < 0 || index >= vertices.value.length) return;
    pushHistory();
    const result = removeVertexAt(vertices.value, legs.value, index);
    vertices.value = result.vertices;
    legs.value = result.legs;
    redraw();
    if (result.rerouteIndex != null && !(await routeLeg(result.rerouteIndex))) {
      revertLastStep();
      notifyBlocked();
      return;
    }
    redraw();
    scheduleAnalytics();
  };

  // ── Вейпоинты ────────────────────────────────────────────────────────

  const waypointMarkerStyle = (waypoint: EditorWaypoint) =>
    POI_TYPE_MARKERS[waypoint.poi_type] ?? POI_TYPE_MARKERS.note;

  const makeWaypointElement = (waypoint: EditorWaypoint): HTMLElement => {
    const style = waypointMarkerStyle(waypoint);
    const el = markerElement(
      "route-map-marker-icon route-editor-waypoint",
      routeMapPinHtml(style.variant, style.glyph, waypoint.name, style.icon),
      PIN_SIZE
    );
    el.classList.toggle("route-editor-waypoint--selected", waypoint.clientId === selectedWaypointId.value);
    el.setAttribute("role", "button");
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      if (mode.value === "erase") {
        deleteWaypoint(waypoint.clientId);
        return;
      }
      selectedLegIndexes.value = [];
      selectedWaypointId.value = selectedWaypointId.value === waypoint.clientId ? null : waypoint.clientId;
      syncWaypointMarkers();
    });
    return el;
  };

  const syncWaypointMarkers = () => {
    const map = getMap();
    if (!map) return;
    const currentIds = new Set(waypoints.value.map((w) => w.clientId));
    for (const [id, marker] of waypointMarkers) {
      if (!currentIds.has(id)) {
        marker.remove();
        waypointMarkers.delete(id);
      }
    }
    for (const waypoint of waypoints.value) {
      let marker = waypointMarkers.get(waypoint.clientId);
      if (!marker) {
        marker = new maplibregl.Marker({
          element: makeWaypointElement(waypoint),
          draggable: true,
          anchor: "bottom",
        }).setLngLat([waypoint.longitude, waypoint.latitude]);
        // Магнит при перетаскивании: маркер "прилипает" к линии/тропе на лету
        // (maplibre сам выставляет позицию до события, мы её поверх
        // переопределяем) и фиксируется там на dragend.
        marker.on("drag", () => {
          if (!magnetEnabled.value) return;
          const ll = marker!.getLngLat();
          const snapped = snapWaypointPoint([ll.lng, ll.lat]);
          if (snapped[0] !== ll.lng || snapped[1] !== ll.lat) marker!.setLngLat(snapped);
        });
        marker.on("dragend", () => {
          let ll = marker!.getLngLat();
          if (magnetEnabled.value) {
            const snapped = snapWaypointPoint([ll.lng, ll.lat]);
            marker!.setLngLat(snapped);
            ll = marker!.getLngLat();
          }
          const target = waypoints.value.find((w) => w.clientId === waypoint.clientId);
          if (target) {
            pushHistory();
            target.longitude = ll.lng;
            target.latitude = ll.lat;
            scheduleAnalytics();
          }
        });
        waypointMarkers.set(waypoint.clientId, marker);
      } else {
        marker.setLngLat([waypoint.longitude, waypoint.latitude]);
        const el = marker.getElement();
        const style = waypointMarkerStyle(waypoint);
        el.innerHTML = routeMapPinHtml(style.variant, style.glyph, waypoint.name, style.icon);
        el.classList.toggle("route-editor-waypoint--selected", waypoint.clientId === selectedWaypointId.value);
      }
      // В ластике пины не перетаскиваются - тап удаляет.
      marker.setDraggable(mode.value !== "erase");
      if (map) marker.addTo(map);
    }
  };

  // Магнит здесь - тот же переключатель, что и для трассировки линии
  // (magnetEnabled): если включён, точка притягивается к ближайшей позиции
  // на уже нарисованной линии. Сам вейпоинт не добавляется - только
  // открывает диалог подтверждения (см. RouteEditorAddWaypointDialog.vue).
  const requestAddWaypointAt = (lngLat: Coordinate) => {
    const target = magnetEnabled.value ? snapWaypointPoint(lngLat) : lngLat;
    pendingWaypoint.value = { latitude: target[1], longitude: target[0] };
  };

  const cancelPendingWaypoint = () => {
    pendingWaypoint.value = null;
    if (mode.value === "waypoint") setMode(null);
  };

  const confirmPendingWaypoint = (poiType: PoiType, name: string, description: string | null) => {
    if (!pendingWaypoint.value) return;
    pushHistory();
    const waypoint: EditorWaypoint = {
      clientId: makeClientId(),
      name,
      description,
      poi_type: poiType,
      latitude: pendingWaypoint.value.latitude,
      longitude: pendingWaypoint.value.longitude,
    };
    waypoints.value.push(waypoint);
    // Не выбирать новую точку автоматически - тип/имя/описание уже заданы в
    // самом диалоге добавления, а показ следом ещё и карточки редактирования
    // (RouteEditorWaypointCard, тот же угол экрана) выглядел бы так, будто
    // диалог не закрылся.
    lastWaypointType.value = poiType;
    pendingWaypoint.value = null;
    // Режим добавления точки одноразовый - после добавления выходим в "правку".
    if (mode.value === "waypoint") mode.value = null;
    updateCursor();
    redraw();
    scheduleAnalytics();
  };

  const updateWaypoint = (clientId: string, patch: Partial<Pick<EditorWaypoint, "name" | "description" | "poi_type">>) => {
    const target = waypoints.value.find((w) => w.clientId === clientId);
    if (!target) return;
    pushHistory();
    Object.assign(target, patch);
    redraw();
    scheduleAnalytics();
  };

  // Закрыть карточку редактирования точки (крестик/"Сохранить") - только
  // снимает выбор, ничего не применяет и не пишет в историю.
  const closeWaypointCard = () => {
    selectedWaypointId.value = null;
    syncWaypointMarkers();
  };

  const deleteWaypoint = (clientId: string) => {
    const index = waypoints.value.findIndex((w) => w.clientId === clientId);
    if (index === -1) return;
    pushHistory();
    waypoints.value.splice(index, 1);
    if (selectedWaypointId.value === clientId) selectedWaypointId.value = null;
    redraw();
    scheduleAnalytics();
  };

  // ── Живая аналитика (POST /routes/preview-analytics) ─────────────────

  const scheduleAnalytics = () => {
    if (analyticsTimer != null) window.clearTimeout(analyticsTimer);
    analyticsTimer = window.setTimeout(fetchAnalytics, 350);
  };

  // Сервер всё равно ресэмплит геометрию шагом 30 м и считает высоты по DEM,
  // поэтому слать ему каждую вершину OSM-рёбер (сотни точек на километры)
  // незачем - упрощение с допуском 8 м почти не меняет дистанцию/набор высоты.
  const ANALYTICS_TOLERANCE_M = 8;
  // GPX: допуск 4 м - линия остаётся на тропе (геометрия уже трассирована по
  // графу), но без тысяч коллинеарных точек.
  const EXPORT_TOLERANCE_M = 4;
  const exportCoordinates = (): Coordinate[] => simplifyCoordinates(renderedCoordinates(), EXPORT_TOLERANCE_M);

  const fetchAnalytics = async () => {
    // Устаревший запрос отменяем: ответ на него всё равно отбрасывается
    // (requestId), а сервер зря считал бы его параллельно с новым.
    analyticsAbort?.abort();
    analyticsAbort = null;
    const points = simplifyCoordinates(renderedCoordinates(), ANALYTICS_TOLERANCE_M);
    if (points.length < 2) {
      analytics.value = null;
      analyticsLoading.value = false;
      return;
    }
    const controller = new AbortController();
    analyticsAbort = controller;
    const requestId = ++analyticsRequestId;
    analyticsLoading.value = true;
    try {
      const { data } = await api.post<RoutePreviewAnalyticsResponse>(
        "/routes/preview-analytics",
        {
          points,
          waypoints: waypoints.value.map((w) => ({
            name: w.name,
            description: w.description,
            poi_type: w.poi_type,
            latitude: w.latitude,
            longitude: w.longitude,
          })),
        },
        { signal: controller.signal }
      );
      if (requestId !== analyticsRequestId) return;
      analytics.value = data;
      // Профиль высот обновился - перерисовываем линию новой интерполяцией
      // высоты (важно только для gradient-режима). Только линию: маркеры вершин и
      // точек от высоты не зависят, а их пересоздание дорого.
      redrawLine();
    } catch (error) {
      // Отменённый запрос (ERR_CANCELED) - не ошибка: аналитику не трогаем.
      if ((error as { code?: string }).code === "ERR_CANCELED") return;
      if (requestId === analyticsRequestId) analytics.value = null;
    } finally {
      if (requestId === analyticsRequestId) analyticsLoading.value = false;
    }
  };

  // ── Обработчики карты ────────────────────────────────────────────────

  const magneticDestinationAt = (lngLat: Coordinate): Coordinate | null => {
    const map = getMap();
    if (!map) return null;
    const p = map.project(lngLat);
    const box: [maplibregl.PointLike, maplibregl.PointLike] = [
      [p.x - 24, p.y - 24],
      [p.x + 24, p.y + 24],
    ];
    const layers = ["trails-line", "roads-line"].filter((id) => Boolean(map.getLayer(id)));
    if (!layers.length) return null;
    const feature = map.queryRenderedFeatures(box, { layers })[0];
    return feature ? lngLat : null;
  };

  // Выбор отрезка тапом по линии (режим правки): ближайший лег в радиусе
  // 14px экрана; тап мимо снимает выбор. Повторный тап по выбранному - тоже.
  const LEG_TAP_PX = 14;
  const selectLegAt = (x: number, y: number) => {
    const map = getMap();
    if (!map) return;
    const index = findNearestLeg(
      legs.value,
      [x, y],
      (c) => {
        const q = map.project(c);
        return [q.x, q.y];
      },
      LEG_TAP_PX
    );
    selectedLegIndexes.value = toggleLegSelection(selectedLegIndexes.value, index);
    redrawLine();
  };

  // Новая вершина линии. routed - класть отрезок по графу (магнит включён и под
  // тапом есть сеть). false - проложить нельзя (на пути река/обрыв, обхода нет):
  // вершина не добавляется, шаг истории не пишется, показывается уведомление.
  const addLineVertex = async (clicked: Coordinate, routed: boolean): Promise<boolean> => {
    const previous = vertices.value.at(-1);
    if (!previous) {
      pushHistory();
      vertices.value.push(clicked);
      redraw();
      return true;
    }
    let geometry: Coordinate[] = [previous, clicked];
    let status: EditorLeg["status"] = "straight";
    if (routed) {
      const result = await routing.route(previous, clicked, { strategy: "shortest", snapRadiusM: 100 });
      if (result?.status === "blocked") {
        notifyBlocked();
        return false;
      }
      if (result) {
        geometry = result.geometry;
        status = result.status === "routed" ? "routed" : "fallback";
      } else {
        status = "fallback";
      }
    }
    pushHistory();
    const endpoint = routed && status === "routed" ? geometry.at(-1)! : clicked;
    vertices.value.push(endpoint);
    legs.value.push({ geometry, routed, status });
    redraw();
    scheduleAnalytics();
    return true;
  };

  const onMapClick = async (e: maplibregl.MapMouseEvent) => {
    if (!active.value || routing.pending.value) return;
    if (suppressNextClick || draggingVertex) {
      suppressNextClick = false;
      return;
    }
    const clicked: Coordinate = [e.lngLat.lng, e.lngLat.lat];

    if (mode.value === "erase") return; // ластик работает по маркерам, не по пустой карте
    if (mode.value === null) {
      if (selectedWaypointId.value) closeWaypointCard();
      selectLegAt(e.point.x, e.point.y);
      return;
    }

    if (mode.value === "waypoint") {
      requestAddWaypointAt(clicked);
      return;
    }

    const routed = magnetEnabled.value && Boolean(magneticDestinationAt(clicked));
    await addLineVertex(clicked, routed);
  };

  // ── Публичное API: режимы/жизненный цикл ──────────────────────────────

  const setLayerVisibility = (visible: boolean) => {
    const map = getMap();
    if (!map || !layersReady) return;
    const v = visible ? "visible" : "none";
    for (const id of LINE_LAYERS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
    }
  };

  const fetchBaseRouteStyle = async () => {
    try {
      const { data } = await api.get<RouteStyleConfig>("/route-style/active");
      baseRouteStyle.value = data;
      lineStyleMode.value = data.mode;
      showDirectionArrows.value = data.arrows.enabled;
    } catch {
      // остаёмся на DEFAULT_ROUTE_STYLE - не критично для редактора
    }
  };

  const setupLayers = () => {
    const map = getMap();
    if (!map || layersReady) return;
    map.addSource(GLOW_SOURCE, { type: "geojson", data: emptyFc() });
    map.addSource(CASING_SOURCE, { type: "geojson", data: emptyFc() });
    map.addSource(ROUTE_EDITOR_LINE_SOURCE, { type: "geojson", data: emptyFc() });
    map.addSource(ARROWS_SOURCE, { type: "geojson", data: emptyFc() });
    map.addSource(LEGSEL_SOURCE, { type: "geojson", data: emptyFc() });

    const paint = buildRouteLinePaint(effectiveStyleConfig.value);
    const layout = buildRouteLineLayout(effectiveStyleConfig.value);
    map.addLayer({
      id: GLOW_LAYER,
      type: "line",
      source: GLOW_SOURCE,
      layout: { visibility: "none", ...layout },
      paint: paint.glow as any,
    });
    map.addLayer({
      id: CASING_LAYER,
      type: "line",
      source: CASING_SOURCE,
      layout: { visibility: "none", ...layout },
      paint: paint.casing as any,
    });
    map.addLayer({
      id: MAIN_LAYER,
      type: "line",
      source: ROUTE_EDITOR_LINE_SOURCE,
      layout: { visibility: "none", ...layout },
      paint: paint.main as any,
    });
    map.addLayer({
      id: LEGSEL_LAYER,
      type: "line",
      source: LEGSEL_SOURCE,
      layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#c9622a", "line-width": 9, "line-opacity": 0.5 },
    });
    if (!map.hasImage(ROUTE_ARROW_IMAGE_ID)) {
      const icon = buildArrowIconImage(effectiveStyleConfig.value.arrows);
      map.addImage(ROUTE_ARROW_IMAGE_ID, { width: icon.width, height: icon.height, data: icon.data }, { pixelRatio: 2 });
    }
    map.addLayer({
      id: ARROWS_LAYER,
      type: "symbol",
      source: ARROWS_SOURCE,
      layout: {
        visibility: "none",
        "icon-image": ROUTE_ARROW_IMAGE_ID,
        "icon-rotate": ["get", "bearing"],
        "icon-rotation-alignment": "map",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });

    map.on("click", onMapClick);
    layersReady = true;
    if (active.value) {
      setLayerVisibility(true);
      updateCursor();
    }
  };

  const resetState = () => {
    selectedLegIndexes.value = [];
    vertices.value = [];
    legs.value = [];
    waypoints.value = [];
    selectedWaypointId.value = null;
    currentRouteId.value = null;
    isCloneOfPublic.value = false;
    dirty.value = false;
    routeMeta.value = { name: "", description: null, tags: [], is_public: false };
    analytics.value = null;
    errorMessage.value = null;
    history.value = [];
    future.value = [];
    routing.reset();
  };

  const newRoute = () => {
    resetState();
    mode.value = "line";
    updateCursor();
    redraw();
  };

  const clearAll = () => {
    if (!vertices.value.length && !waypoints.value.length) return;
    pushHistory();
    vertices.value = [];
    legs.value = [];
    waypoints.value = [];
    selectedWaypointId.value = null;
    redraw();
    scheduleAnalytics();
  };

  // Переключатель отрезков "прямая / по тропам" (для выбранных тапом легов).
  // Одна запись в истории на всё действие, а не по одной на отрезок: undo
  // откатывает переключение целиком.
  const setLegsRouted = async (indexes: readonly number[], routed: boolean) => {
    const targets = indexes.filter((i) => legs.value[i] && legs.value[i].routed !== routed);
    if (!targets.length) return;
    pushHistory();
    for (const index of targets) {
      const leg = legs.value[index];
      legs.value[index] = routed
        ? { ...leg, routed: true, status: "straight" }
        : { geometry: [vertices.value[index], vertices.value[index + 1]], routed: false, status: "straight" };
    }
    redraw();
    const blocked: number[] = [];
    if (routed) {
      for (const index of targets) if (!(await routeLeg(index))) blocked.push(index);
    }
    if (blocked.length) {
      notifyBlocked();
      if (blocked.length === targets.length) {
        revertLastStep();
        return;
      }
      // Часть отрезков проложить не удалось - они остаются прямыми.
      for (const index of blocked) {
        legs.value[index] = { geometry: [vertices.value[index], vertices.value[index + 1]], routed: false, status: "straight" };
      }
    }
    redraw();
    scheduleAnalytics();
  };
  const setLegRouted = (index: number, routed: boolean) => setLegsRouted([index], routed);

  // Переключатель магнита: кроме флага для новых отрезков, сразу меняет
  // последний отрезок - выключил, и он выпрямился; включил, и лёг на тропы
  // заново. Остальные отрезки не трогаем (их правят по одному, см. setLegRouted).
  const setMagnet = async (enabled: boolean) => {
    if (magnetEnabled.value === enabled) return;
    magnetEnabled.value = enabled;
    if (active.value && legs.value.length > 0) await setLegRouted(legs.value.length - 1, enabled);
  };

  // Сводка по выбранным отрезкам: сколько, суммарная длина и "по тропам" ли
  // они все (тогда кнопка переключает их в прямую, иначе - все на тропы).
  const selectedLegInfo = computed(() => {
    const picked = selectedLegIndexes.value.map((i) => ({ i, leg: legs.value[i] })).filter((x) => x.leg);
    if (!picked.length) return null;
    let meters = 0;
    for (const { leg } of picked) {
      for (let i = 1; i < leg.geometry.length; i++) {
        meters += haversineDistanceM(leg.geometry[i - 1][1], leg.geometry[i - 1][0], leg.geometry[i][1], leg.geometry[i][0]);
      }
    }
    return {
      indexes: picked.map((x) => x.i),
      count: picked.length,
      routed: picked.every((x) => x.leg.routed),
      lengthM: meters,
    };
  });

  // Замкнуть маршрут: отрезок от финиша к старту (по тропам, если включён магнит).
  const canCloseLoop = computed(() => vertices.value.length >= 3 && !isClosedLine(vertices.value));
  const closeLoop = async () => {
    if (!canCloseLoop.value) return;
    pushHistory();
    const first = vertices.value[0];
    const last = vertices.value[vertices.value.length - 1];
    const routed = magnetEnabled.value;
    vertices.value.push([first[0], first[1]]);
    legs.value.push({ geometry: [last, first], routed, status: "straight" });
    redraw();
    if (routed && !(await routeLeg(legs.value.length - 1))) {
      revertLastStep();
      notifyBlocked();
      return;
    }
    redraw();
    scheduleAnalytics();
  };

  // Притянуть выбранную точку к линии/тропе сейчас (не зависит от магнита).
  const snapSelectedWaypoint = () => {
    const target = waypoints.value.find((w) => w.clientId === selectedWaypointId.value);
    if (!target) return;
    const snapped = snapWaypointPoint([target.longitude, target.latitude]);
    if (snapped[0] === target.longitude && snapped[1] === target.latitude) return;
    pushHistory();
    target.longitude = snapped[0];
    target.latitude = snapped[1];
    syncWaypointMarkers();
    scheduleAnalytics();
  };

  const deleteLastVertex = () => {
    if (!vertices.value.length) return;
    pushHistory();
    vertices.value.pop();
    legs.value.pop();
    redraw();
    scheduleAnalytics();
  };

  const activate = () => {
    active.value = true;
    trackEvent("RouteEditor: Activate");
    setupLayers();
    if (baseRouteStyle.value === DEFAULT_ROUTE_STYLE) void fetchBaseRouteStyle();
    setLayerVisibility(true);
    redraw();
    updateCursor();
  };

  // «Старт отсюда» (инструмент «Метка»): первая вершина линии. Не затирает
  // уже нарисованный черновик - возвращает false, вызывающий скажет об этом.
  const startAt = (coord: Coordinate): boolean => {
    if (vertices.value.length > 0) return false;
    pushHistory();
    vertices.value.push(coord);
    mode.value = "line";
    redraw();
    scheduleAnalytics();
    return true;
  };

  const deactivate = () => {
    // Выход (после сохранения или подтверждённого «Сбросить») не оставляет
    // черновик: следующий вход - с чистого листа.
    resetState();
    mode.value = "line";
    active.value = false;
    setLayerVisibility(false);
    // Маркеры вершин и точек - DOM-элементы поверх карты, слои их не прячут.
    removeAllMarkers();
    const map = getMap();
    if (map) map.getCanvas().style.cursor = "";
  };

  const setMode = (next: EditorMode) => {
    mode.value = next;
    selectedWaypointId.value = null;
    selectedLegIndexes.value = [];
    updateCursor();
    syncVertexMarkers();
    syncWaypointMarkers();
  };

  // Повторное нажатие уже включённой кнопки отжимает её (режим "правка").
  const toggleMode = (next: Exclude<EditorMode, null>) => {
    setMode(mode.value === next ? null : next);
  };

  // ── Загрузка/клонирование/импорт/сохранение ───────────────────────────

  // Превращает уже загруженную детальную геометрию (сохранённый маршрут -
  // пересэмплирован бэкендом шагом ~30м, geometry.py::_resample_points; или
  // GPX-импорт - обычно записан GPS-треком с ~1-5м между точками) в
  // редактируемую модель "вершины + леги", ту же, что и у линии, нарисованной
  // вручную - вместо неинтерактивной "основы" (baseLineCoordinates), как было
  // раньше. Упрощение Дугласа-Пекера (utils/simplifyPath.ts) выбирает
  // разумное число вершин; геометрия каждого лега - точный срез исходных
  // точек между соседними сохранёнными вершинами (routed:false/"straight" -
  // это не наш маршрутизатор, а реальный трек), так что форма линии не
  // теряется, просто немногие точки становятся перетаскиваемыми узлами.
  const buildEditableGeometryFromPath = (coords: Coordinate[]): { vertices: Coordinate[]; legs: EditorLeg[] } => {
    if (coords.length < 2) return { vertices: coords.slice(), legs: [] };
    const indices = simplifyLineIndicesCapped(coords);
    const newVertices = indices.map((i) => coords[i]);
    const newLegs: EditorLeg[] = [];
    for (let k = 0; k < indices.length - 1; k++) {
      newLegs.push({
        geometry: coords.slice(indices[k], indices[k + 1] + 1),
        routed: false,
        status: "straight",
      });
    }
    return { vertices: newVertices, legs: newLegs };
  };

  const applyLoadedRoute = (route: RouteMapItem, asNewCopy: boolean) => {
    resetState();
    const built = buildEditableGeometryFromPath((route.coordinates || []).map((c) => [c[0], c[1]] as Coordinate));
    vertices.value = built.vertices;
    legs.value = built.legs;
    waypoints.value = (route.pois || []).map((poi) => ({
      clientId: makeClientId(),
      name: poi.name,
      description: poi.description ?? null,
      poi_type: isPoiType(poi.poi_type) ? poi.poi_type : "note",
      latitude: poi.latitude,
      longitude: poi.longitude,
    }));
    currentRouteId.value = asNewCopy ? null : route.id;
    isCloneOfPublic.value = asNewCopy;
    dirty.value = false;
    routeMeta.value = {
      name: asNewCopy ? `${route.name} (копия)` : route.name,
      description: route.description ?? null,
      tags: route.tags || [],
      is_public: asNewCopy ? false : Boolean(route.is_public),
    };
    mode.value = null;
    updateCursor();
    redraw();
    scheduleAnalytics();
  };

  const loadRoute = async (routeId: number, opts: { asNewCopy?: boolean } = {}) => {
    loading.value = true;
    errorMessage.value = null;
    try {
      const { data } = await api.get<RouteMapItem>(`/routes/${routeId}`);
      applyLoadedRoute(data, Boolean(opts.asNewCopy));
      return data;
    } catch {
      errorMessage.value = ctx.t("map.routeEditor.loadError");
      return null;
    } finally {
      loading.value = false;
    }
  };

  // Импорт из GPX-файла (клиентский разбор, см. utils/gpxImport.ts) - всегда
  // как новый черновик (currentRouteId=null), файл никогда не соответствует
  // существующему маршруту в БД.
  const loadFromGpxImport = (name: string, points: Coordinate[], waypointsIn: GpxImportWaypoint[]) => {
    resetState();
    const built = buildEditableGeometryFromPath(points.map((p) => [p[0], p[1]] as Coordinate));
    vertices.value = built.vertices;
    legs.value = built.legs;
    waypoints.value = waypointsIn.map((w) => ({
      clientId: makeClientId(),
      name: w.name,
      description: w.description ?? null,
      poi_type: isPoiType(w.poi_type) ? w.poi_type : "note",
      latitude: w.latitude,
      longitude: w.longitude,
    }));
    dirty.value = true;
    routeMeta.value = { name, description: null, tags: [], is_public: false };
    mode.value = null;
    updateCursor();
    redraw();
    scheduleAnalytics();
  };

  const SAVE_MAX_POINTS = 4500; // с запасом до лимита сервера (5000)
  const buildSavePayload = (meta: RouteSaveMeta) => ({
    name: meta.name,
    description: meta.description,
    tags: meta.tags,
    is_public: meta.is_public,
    snap_to_trails: false,
    // Сервер принимает до 5000 точек (иначе 422): длинный маршрут по плотной
    // геометрии упрощаем, короткий уходит как есть.
    points: simplifyToMaxPoints(renderedCoordinates(), SAVE_MAX_POINTS),
    waypoints: waypoints.value.map((w) => ({
      name: w.name,
      description: w.description,
      poi_type: w.poi_type,
      latitude: w.latitude,
      longitude: w.longitude,
    })),
  });

  const save = async (meta: RouteSaveMeta, opts: { asNew?: boolean } = {}): Promise<RouteMapItem | null> => {
    if (renderedCoordinates().length < 2) {
      errorMessage.value = ctx.t("map.routeEditor.needTwoPoints");
      return null;
    }
    saving.value = true;
    errorMessage.value = null;
    try {
      const payload = buildSavePayload(meta);
      const targetId = opts.asNew ? null : currentRouteId.value;
      const { data } =
        targetId != null
          ? await api.patch<RouteMapItem>(`/routes/${targetId}`, payload)
          : await api.post<RouteMapItem>("/routes", payload);
      currentRouteId.value = data.id;
      isCloneOfPublic.value = false;
      dirty.value = false;
      routeMeta.value = { ...meta };
      trackEvent("RouteEditor: Save");
      return data;
    } catch {
      errorMessage.value = ctx.t("map.routeEditor.saveError");
      return null;
    } finally {
      saving.value = false;
    }
  };

  const destroy = () => {
    if (analyticsTimer != null) window.clearTimeout(analyticsTimer);
    analyticsAbort?.abort();
    analyticsAbort = null;
    removeAllMarkers();
    const map = getMap();
    if (map) map.off("click", onMapClick);
  };

  return {
    active,
    mode,
    magnetEnabled,
    setMagnet,
    legs,
    lastWaypointType,
    selectedWaypointId,
    selectedLegIndexes,
    selectedLegInfo,
    setLegsRouted,
    canCloseLoop,
    setLegRouted,
    closeLoop,
    addLineVertex,
    beginVertexDrag,
    dragVertexTo,
    finishVertexDrag,
    deleteVertex,
    notice,
    snapSelectedWaypoint,
    pendingWaypoint,
    waypoints,
    vertices,
    routingPending: routing.pending,
    analytics,
    analyticsLoading,
    currentRouteId,
    isCloneOfPublic,
    dirty,
    routeMeta,
    loading,
    saving,
    errorMessage,
    canUndo,
    canRedo,
    undo,
    redo,
    reverseRoute,
    setMode,
    toggleMode,
    setupLayers,
    activate,
    deactivate,
    startAt,
    newRoute,
    clearAll,
    deleteLastVertex,
    updateWaypoint,
    deleteWaypoint,
    closeWaypointCard,
    confirmPendingWaypoint,
    cancelPendingWaypoint,
    loadRoute,
    loadFromGpxImport,
    save,
    renderedCoordinates,
    exportCoordinates,
    syncVertexMarkers,
    destroy,
    // Вид
    lineStyleMode,
    showDirectionArrows,
    showStatsPanel,
    setLineStyleMode,
    setShowDirectionArrows,
  };
}

function emptyFc(): FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}
